import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './MusicPlayer.css';

/* Единый плавающий плеер фоновой музыки для всех шаблонов.
   Пропы:
     music = { track:{url,title}, autoplay, volume, fadeIn, player:{style,color,corner} }
     isPreview — в превью (чат-бот) НИКОГДА не проигрываем, только показываем кнопку.

   Браузеры блокируют autoplay со звуком без действия пользователя, поэтому при
   autoplay мы стартуем при первом взаимодействии гостя (клик/тач/скролл/клавиша). */

function isLight(hex) {
  const c = (hex || '').replace('#', '');
  if (c.length < 6) return false;
  const r = parseInt(c.slice(0, 2), 16), g = parseInt(c.slice(2, 4), 16), b = parseInt(c.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) > 150;
}

export default function MusicPlayer({ music, palette, isPreview = false }) {
  const url = music?.track?.url || '';
  const audioRef = useRef(null);
  const fadeRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [hint, setHint] = useState('in'); // 'in' | 'out' | null — бабл-подсказка

  const targetVol = Math.min(1, Math.max(0, music?.volume ?? 0.7));
  const fadeIn = !!music?.fadeIn;
  const autoplay = !!music?.autoplay;
  /* В полноэкранном превью (мобилка) разрешаем автозапуск как на сайте. */
  const previewPlay = !!music?.previewPlay;
  const corner = music?.player?.corner || 'br';
  /* Цвет кнопки берётся из палитры дизайна (шаг «Палитра»), не из настроек
     плеера: фон — основной цвет, иконка — контрастная. */
  const color = palette?.primary || palette?.accent || '#1a1a1a';

  const clearFade = () => { if (fadeRef.current) { clearInterval(fadeRef.current); fadeRef.current = null; } };

  const rampTo = (to, ms = 2500) => {
    const el = audioRef.current; if (!el) return;
    clearFade();
    const from = el.volume;
    const steps = 24; let i = 0;
    fadeRef.current = setInterval(() => {
      i += 1;
      el.volume = Math.min(1, Math.max(0, from + (to - from) * (i / steps)));
      if (i >= steps) { el.volume = to; clearFade(); }
    }, ms / steps);
  };

  const startPlay = () => {
    const el = audioRef.current; if (!el || !url) return;
    el.volume = fadeIn ? 0 : targetVol;
    const p = el.play();
    if (p && p.catch) p.then(() => { if (fadeIn) rampTo(targetVol); }).catch(() => {});
    else if (fadeIn) rampTo(targetVol);
  };

  const toggle = () => {
    setHint(null);
    /* Клик — явное действие пользователя, поэтому играем и в превью
       (автозапуск в превью по-прежнему отключён — только по нажатию). */
    const el = audioRef.current; if (!el || !url) return;
    if (el.paused) startPlay(); else { clearFade(); el.pause(); }
  };

  /* Автозапуск по первому взаимодействию (опубликованный сайт, а также
     полноэкранное превью в мобилке — previewPlay). */
  useEffect(() => {
    if ((isPreview && !previewPlay) || !autoplay || !url) return;
    let done = false;
    const evs = ['pointerdown', 'touchstart', 'keydown', 'scroll'];
    const onFirst = () => {
      if (done) return; done = true;
      cleanup();
      const el = audioRef.current;
      if (el && el.paused) startPlay();
    };
    const cleanup = () => evs.forEach(e => window.removeEventListener(e, onFirst));
    evs.forEach(e => window.addEventListener(e, onFirst, { passive: true }));
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreview, previewPlay, autoplay, url]);

  useEffect(() => () => clearFade(), []);

  /* Закрыли полноэкранное превью (previewPlay → false) — глушим музыку. */
  useEffect(() => {
    if (isPreview && !previewPlay) {
      const el = audioRef.current;
      if (el && !el.paused) { clearFade(); el.pause(); }
    }
  }, [isPreview, previewPlay]);

  /* Бабл-подсказка «Включить музыку для атмосферы»: всплывает как чат,
     держится несколько секунд и исчезает — остаётся только кнопка. */
  useEffect(() => {
    if (!url) return;
    const t1 = setTimeout(() => setHint('out'), 5000);
    const t2 = setTimeout(() => setHint(null), 5500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [url]);
  /* Заиграла музыка — подсказка больше не нужна. */
  useEffect(() => { if (playing) setHint(null); }, [playing]);

  /* В превью находим скролл-контейнер рамки (мобильный телефон / десктоп-
     браузер из LivePreview) и держим кнопку в его видимом углу при скролле —
     иначе containment страницы «приклеивает» её к контенту. */
  const anchorRef = useRef(null);
  const boxRef = useRef(null);
  const [host, setHost] = useState(null);

  useEffect(() => {
    if (!isPreview || !url) return;
    const a = anchorRef.current;
    const h = a && a.closest('.lp-phone-screen, .lp-content');
    if (!h) return;
    try { if (getComputedStyle(h).position === 'static') h.style.position = 'relative'; } catch (_) {}
    setHost(h);
  }, [isPreview, url]);

  useEffect(() => {
    if (!isPreview || !host) return;
    const place = () => {
      const box = boxRef.current; if (!box) return;
      const padX = 14, padY = 68;
      const w = box.offsetWidth, ht = box.offsetHeight;
      const top = corner[0] === 't' ? host.scrollTop + padY : host.scrollTop + host.clientHeight - ht - padY;
      const left = corner[1] === 'l' ? padX : host.clientWidth - w - padX;
      box.style.top = `${top}px`;
      box.style.left = `${left}px`;
    };
    place();
    const raf = requestAnimationFrame(place);
    host.addEventListener('scroll', place, { passive: true });
    window.addEventListener('resize', place);
    return () => { cancelAnimationFrame(raf); host.removeEventListener('scroll', place); window.removeEventListener('resize', place); };
  }, [isPreview, host, corner, playing, url]);

  if (!url) return null;

  const tint = isLight(color) ? '#1a1a1a' : color; // на стекле иконка-акцент

  const content = (
    <>
      <audio
        ref={audioRef}
        src={url}
        loop
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />
      {hint && (
        <span className={`mp-hint mp-hint--${hint}`} aria-hidden="true">
          Включить музыку<br />для атмосферы
        </span>
      )}
      <button
        type="button"
        className={`mp-btn${playing ? ' mp-btn--playing' : ''}`}
        style={{ '--mp-color': color, '--mp-tint': tint }}
        onClick={toggle}
        aria-label={playing ? 'Пауза' : 'Включить музыку'}
        title={music?.track?.title || 'Музыка'}
      >
        <span className="mp-ring" aria-hidden="true" />
        <span className="mp-pulse" aria-hidden="true" />
        <span className="mp-gloss" aria-hidden="true" />
        <span className="mp-ico" aria-hidden="true">
          {playing
            ? <span className="mp-eq"><i /><i /><i /><i /></span>
            : (
              <svg className="mp-svg" viewBox="0 0 32 32" width="22" height="22" fill="none">
                <path d="M12 9.8c0-1.4 1.5-2.2 2.7-1.5l8.2 5.2c1.1.7 1.1 2.3 0 3L14.7 21c-1.2.7-2.7-.1-2.7-1.5V9.8z"
                  fill="currentColor" stroke="currentColor" strokeWidth="2.4" strokeLinejoin="round" />
              </svg>
            )}
        </span>
      </button>
    </>
  );

  /* Опубликованный сайт: портал в <body> → fixed относительно вьюпорта
     (минуя container-type: inline-size страниц-шаблонов). */
  if (!isPreview && typeof document !== 'undefined') {
    return createPortal(
      <div className={`mp mp--${corner}`}>{content}</div>,
      document.body
    );
  }

  /* Превью: плавающая кнопка в скролл-контейнере рамки (absolute, позиция
     обновляется на скролл). Фолбэк — inline, если контейнер не найден. */
  return (
    <>
      <span ref={anchorRef} style={{ display: 'none' }} aria-hidden="true" />
      {host
        ? createPortal(<div ref={boxRef} className={`mp mp--abs mp--${corner}`}>{content}</div>, host)
        : <div className={`mp mp--${corner}`}>{content}</div>}
    </>
  );
}
